import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { PackingStationsApiAdapter } from '../adapters/PackingStationsApiAdapter';
import { Filters } from '../models/Filters';
import { Service } from '../services/Service';

export const usePackingStations = (filters: Filters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new PackingStationsApiAdapter(httpClient);
  const service = new Service(apiAdapter);

  // Estado para controlar si es la primera carga real
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Estado para mantener datos anteriores durante búsquedas
  const [previousData, setPreviousData] = useState<any[]>([]);

  const { data, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isInitialLoading } =
    useInfiniteQuery({
      queryKey: ['packing-stations', filters],
      queryFn: ({ pageParam = 1 }) =>
        service.getPaginated({
          ...filters,
          page: pageParam,
        }),
      getNextPageParam: (lastPage) => lastPage.nextPage,
      initialPageParam: 1,
      staleTime: 2 * 60 * 1000, // 2 minutos
      gcTime: 5 * 60 * 1000, // 5 minutos
      placeholderData: (previousQueryData) => previousQueryData,
    });

  const packingStations = useMemo(() => data?.pages.flatMap((page) => page.packingStations) ?? [], [data]);

  // Total real del API
  const totalFromApi = data?.pages?.[0]?.total ?? 0;

  // Marcar que ya no es la primera carga
  useEffect(() => {
    if (packingStations.length > 0 && isFirstLoad) {
      setIsFirstLoad(false);
    }
  }, [packingStations.length, isFirstLoad]);

  // Mantener datos anteriores durante búsquedas
  useEffect(() => {
    if (packingStations.length > 0) {
      setPreviousData(packingStations);
    }
  }, [packingStations]);

  // Usar datos anteriores durante fetching para evitar parpadeos
  const displayData = isFetching && !isFirstLoad && previousData.length > 0 ? previousData : packingStations;

  return {
    packingStations: displayData,
    isLoading: isFirstLoad && isInitialLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    total: totalFromApi,
  };
};
